<script setup lang="ts">
/**
 * ⚔️→🖼️ Inline-icon text (v2.24, per Javi: "storm tempo and pacesetter
 * need new icon for attack"): renders a plain data string (skill blurbs,
 * passive tier text) with known stat emoji swapped for their painted
 * icons. Data files stay plain text — tooltips and any non-rich context
 * keep the emoji for free.
 */
const props = defineProps<{
  text: string
  /** 🗺️ v7.38: per-use overrides laid over ICONS (the map legend paints the
   * map's own node art for ⛺/🧩 without changing what they render elsewhere). */
  icons?: Record<string, string>
}>()

// v3.15 (per Javi): EVERY stat/system emoji with a painted icon swaps.
// Multi-codepoint sequences must come before their bare forms — the
// alternation is built in insertion order.
const ICONS: Record<string, string> = {
  '⚔️': '/ui/icon-attack.webp',
  '⚔': '/ui/icon-attack.webp',
  '👟': '/ui/icon-speed.webp',
  // The node goal counter wears the victory medal (v2.14) — blurbs
  // referencing the ⭐ goal show the same painted icon (v2.27).
  '⭐': '/ui/icon-victory.webp',
  '⏳': '/ui/icon-cooldown.webp',
  '⌛': '/ui/icon-cooldown.webp',
  '🪙': '/ui/icon-gold.webp',
  '🌟': '/ui/stardust.webp',
  '🩸': '/ui/icon-blood.webp',
  '🤕': '/ui/icon-injury.webp',
  // v3.46 (Javi's emoji audit): every remaining system glyph that has
  // painted art now swaps here too, so any text routed through IconText
  // picks it up for free.
  '💰': '/ui/icon-gold.webp',
  '🏆': '/ui/icon-trophy.webp',
  '✅': '/ui/icon-succeeded.webp',
  '🧩': '/ui/icon-puzzle.webp',
  '💢': '/ui/icon-damage.webp',
  '🎓': '/ui/rewards/trainingground.webp',
  '🎒': '/ui/icon-relics.webp',
  '⛺': '/ui/rewards/reststop.webp',
  '🎉': '/ui/icon-celebrate.webp',
  '🔍': '/ui/icon-inspect.webp',
  // v3.47 (art by Javi): boss crest, elite crown, apotheosis, the
  // feint masks, the mission split and the safety net.
  '👹': '/ui/icon-boss.webp',
  '👑': '/ui/icon-elite.webp',
  '🌌': '/ui/icon-apotheosis.webp',
  '🎭': '/ui/icon-feint.webp',
  '💥': '/ui/icon-split.webp',
  '🕸️': '/ui/icon-safetynet.webp?v=3',
  '🕸': '/ui/icon-safetynet.webp?v=3',
  // v3.81 (Javi: "update it with all the proper icons, its still using
  // emojis"): the tutorial's remaining glyphs. Each one already had
  // painted art in use elsewhere as its `alt` twin — HP wears the heart,
  // discard wears the bin, the route planner wears the pencil — so these
  // are pairings the game had already made, just never routed here.
  '❤️': '/ui/heart.webp',
  '❤': '/ui/heart.webp',
  '🗑️': '/ui/icon-trash.webp',
  '🗑': '/ui/icon-trash.webp',
  '✏️': '/ui/icon-pencil.webp',
  '✏': '/ui/icon-pencil.webp',
  // Map-node glyphs take their own node art — this is what the player
  // taps on the road, so it is the picture they should be shown.
  '🛒': '/ui/nodes/shop.webp',
  '🎁': '/ui/nodes/reward.webp',
  '❓': '/ui/nodes/mystery.webp',
  // The encounter's basecamp. ⛺ stays on the Rest Stop REWARD art above;
  // 🏕️ is the camp on the field where you pause — v5.07 (per Javi) it
  // takes its OWN painted camp icon, not the rest node's tent.
  '🏕️': '/ui/icon-camp.webp',
  '🏕': '/ui/icon-camp.webp',
  // ⏫ v5.07: the enrage face, for the escalation tutorial tip.
  '😡': '/ui/icon-enrage.webp',
  // 🧭 v5.12: the banner tour points at the map button.
  '🗺️': '/ui/icon-map.webp',
  '⏸': '/ui/icon-pause.webp',
  '⏩': '/ui/icon-fastforward.webp',
  '🗺': '/ui/icon-map.webp',
  '🔥': '/elements/fire-v4.png',
  '💧': '/elements/water-v4.png',
  '🌿': '/elements/grass-v4.png',
  '⚡': '/elements/lightning-v4.png',
  '🪨': '/elements/earth-v6.png',
}

// An override of a known glyph keeps its place in the alternation (spread
// preserves key order), so multi-codepoint-first still holds.
const glyphs = computed<Record<string, string>>(() => (props.icons ? { ...ICONS, ...props.icons } : ICONS))
const parts = computed(() => {
  const pattern = new RegExp(`(${Object.keys(glyphs.value).join('|')})`, 'g')
  return props.text.split(pattern).filter(p => p !== '')
})
</script>

<template>
  <span class="icon-text"><template v-for="(part, i) in parts" :key="i"><img
    v-if="glyphs[part]"
    :src="glyphs[part]"
    :alt="part"
    class="stat-img"
    draggable="false"
  ><template v-else>{{ part }}</template></template></span>
</template>
